import { useState, useEffect, useCallback } from 'react';

const THEME_KEY = 'lists_theme';              // 'dark' | 'light'
const PALETTE_KEY = 'lists_palette';          // 'default' | 'tech'
const FONT_SIZE_KEY = 'lists_font_size';      // integer px
const LINE_HEIGHT_KEY = 'lists_line_height';  // float (unitless)

// Walter 2026-05-13: light is the new default — friendlier first
// impression for new users + matches the landing page's cream
// palette. Existing users keep whatever they last had via the
// localStorage value in readTheme below.
const DEFAULT_THEME = 'light';
const DEFAULT_PALETTE = 'default';
const DEFAULT_FONT_SIZE = 12;
const DEFAULT_LINE_HEIGHT = 1.5;

function readTheme() {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : DEFAULT_THEME;
  } catch { return DEFAULT_THEME; }
}

function readPalette() {
  try {
    const v = localStorage.getItem(PALETTE_KEY);
    // Accept current values plus legacy ones from before the rename
    // ('warm' → 'default', 'classic' → 'tech') so saved prefs migrate
    // cleanly the first time the app loads after the rename.
    if (v === 'default' || v === 'warm') return 'default';
    if (v === 'tech'    || v === 'classic') return 'tech';
    return DEFAULT_PALETTE;
  } catch { return DEFAULT_PALETTE; }
}

function readFontSize() {
  try {
    const v = parseInt(localStorage.getItem(FONT_SIZE_KEY) || '', 10);
    return Number.isFinite(v) && v >= 8 && v <= 32 ? v : DEFAULT_FONT_SIZE;
  } catch { return DEFAULT_FONT_SIZE; }
}

function readLineHeight() {
  try {
    const v = parseFloat(localStorage.getItem(LINE_HEIGHT_KEY) || '');
    return Number.isFinite(v) && v >= 1.0 && v <= 2.5 ? v : DEFAULT_LINE_HEIGHT;
  } catch { return DEFAULT_LINE_HEIGHT; }
}

/**
 * useSettings
 *
 * Lightweight preference store backed by localStorage. Values are applied
 * to <html> as a data-theme attribute and CSS custom properties so the rest
 * of the app can pick them up without any prop threading.
 */
export function useSettings() {
  const [theme, setThemeState] = useState(readTheme);
  const [palette, setPaletteState] = useState(readPalette);
  const [fontSize, setFontSizeState] = useState(readFontSize);
  const [lineHeight, setLineHeightState] = useState(readLineHeight);

  // Apply theme -> html[data-theme]
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') root.setAttribute('data-theme', 'light');
    else root.removeAttribute('data-theme');
    try { localStorage.setItem(THEME_KEY, theme); } catch {}
  }, [theme]);

  // Apply palette -> html.palette-tech (default has no class).
  // CSS in index.css overrides color tokens under this class for the
  // green-on-charcoal "tech" look.
  useEffect(() => {
    const root = document.documentElement;
    if (palette === 'tech') root.classList.add('palette-tech');
    else root.classList.remove('palette-tech');
    try { localStorage.setItem(PALETTE_KEY, palette); } catch {}
  }, [palette]);

  // Apply font size -> html style --note-font-size (used inside note bodies)
  // plus --ui-scale (a unitless multiplier used to scale chrome text + controls
  // everywhere in the app, so the setting affects the whole UI, not just
  // tile content). Scale is relative to the M (default) size of 12px.
  useEffect(() => {
    document.documentElement.style.setProperty('--note-font-size', `${fontSize}px`);
    document.documentElement.style.setProperty('--ui-scale', String(fontSize / DEFAULT_FONT_SIZE));
    try { localStorage.setItem(FONT_SIZE_KEY, String(fontSize)); } catch {}
  }, [fontSize]);

  // Apply line height -> html style --note-line-height
  useEffect(() => {
    document.documentElement.style.setProperty('--note-line-height', String(lineHeight));
    try { localStorage.setItem(LINE_HEIGHT_KEY, String(lineHeight)); } catch {}
  }, [lineHeight]);

  const setTheme = useCallback((v) => setThemeState(v), []);
  const setPalette = useCallback((v) => setPaletteState(v), []);
  const setFontSize = useCallback((v) => setFontSizeState(v), []);
  const setLineHeight = useCallback((v) => setLineHeightState(v), []);

  return {
    theme, setTheme,
    palette, setPalette,
    fontSize, setFontSize,
    lineHeight, setLineHeight,
  };
}
